import React, { Children } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from "react-router";
import { Link } from 'react-router-dom';
import { ApplicationState } from '../../../store';
import { CertificationConsolidationResponse } from '../../../store/Models/Api/Surveys/CertificationConsolidationApiModels';
import { Certification, CertificationBase } from '../../../store/Models/Certifications';
import { CertificationType } from '../../../store/Models/Documents/DocumentModels';
import { FullInventory } from '../../../store/Models/Inventories/FullInventory';
import { SimpleInventory } from '../../../store/Models/Inventories/SimpleInventory';
import { SharedMaterialCoreData } from '../../../store/Models/SharedMaterials/DataModel';
import { LibraryInventoriesGetInventory, ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import SaveDataManager from '../../../utilities/SaveDataManager';
import { ContentSeparator } from '../../core/ContentSeparator';
import { DropdownMenu } from '../../core/DropdownMenu';
import { LoadingSpinner, LoadingSpinnerSize } from '../../core/LoadingSpinner';
import SelectLibraryInventory from '../../core/SelectLibraryInventory';
import { ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import Layout from '../../Layout';
import { OfflineProcessProgress } from '../SurveyOfflineProgressModal';
import { ToxnotMainApplicationViewComponent } from '../../core/BaseApplicationComponents/ToxnotMainApplicationViewComponent';
import { Heading } from '../../core/Heading/Heading';
import { ContentContainer } from '../../core/ContentContainer/ContentContainer';
import CertificationRendererManager from '../../../store/Managers/CertificationRendererManager';

import "./styles.css"

export type Props = ToxnotComponentProperties &
    RouteComponentProps<{ responseId: string }>;

enum UiState {
    Loading,
    Failed,
    Editing,
    SelectLibraryInventory,
    CreateLibraryInventory,
    InitiatingSubmission,
    SubmissionInProgress,
    SubmissionComplete,
    NewProductNameBlank
}

interface NewProductInfo {
    name: string;
    description: string;
    supplier: string;
    plant: string;
}

interface LocalState {
    uiState: UiState;
    response: CertificationConsolidationResponse | null;
    allSharedMaterialsTable: { [sharedMaterialId: string]: SharedMaterialCoreData };
    selectedLibraryInventories: FullInventory[];
    requestInventoryIdsToSelectedLibraryInventoryMap: { [requestInventoryId: string]: FullInventory }
    newProduct: NewProductInfo;
    submissionOfflineProcessId: string | null;
}

// ===============================================================================

class CertificationConsolidationResponseEditor extends ToxnotMainApplicationViewComponent<Props, UiState, LocalState, CertificationConsolidationResponse> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            uiState: UiState.Loading,
            response: null,
            allSharedMaterialsTable: {},
            selectedLibraryInventories: [],
            requestInventoryIdsToSelectedLibraryInventoryMap: {},
            newProduct: {
                name: "",
                description: "",
                supplier: "",
                plant: ""
            },
            submissionOfflineProcessId: null
        };
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    protected getPostInitialLoadUiState() {
        return UiState.Editing;
    }

    // ===============================================================================

    protected async fetchViewDataAndRefreshState() {
        const responseId = this.props.match.params.responseId;

        const getResponseApiResult = await this._serviceClient.getCertificationConsolidationResponse(responseId);

        if (!getResponseApiResult.success) {
            this.enterFailedState();
            return;
        }

        const response = getResponseApiResult.data!;

        const sharedMaterialsApiResult = await this._serviceClient.sharedMaterialsGetForTeam();

        if (!sharedMaterialsApiResult.success) {
            this.enterFailedState();
            return;
        }

        const inventoryResponsesThatHaveAssignedRespondedInventories = response
            .responseData
            .inventoryResponses
            .filter(inventoryResponse => inventoryResponse.responderLibraryInventoryId !== null);

        const distinctResponderInventoryIds = inventoryResponsesThatHaveAssignedRespondedInventories.map(r => r.responderLibraryInventoryId!).distinct();

        const selectedLibraryInventories: FullInventory[] = [];

        for (let responderInventoryId of distinctResponderInventoryIds) {
            const getInventoryResult = await this._serviceClient.getInventory(responderInventoryId);

            if (!getInventoryResult.success) {
                this.enterFailedState();
                return;
            }

            selectedLibraryInventories.push(getInventoryResult.data!);
        }

        const requestInventoryIdsToSelectedLibraryInventoryMap: { [invId: string]: FullInventory } = inventoryResponsesThatHaveAssignedRespondedInventories
            .reduce((curr, inventoryResponse) => {
                const fullInv = selectedLibraryInventories.firstOrDefault(inv => inv.inventoryId === inventoryResponse.responderLibraryInventoryId);

                if (fullInv) {
                    curr[inventoryResponse.requestorLibraryInventoryId] = fullInv;
                }

                return curr;
            }, {} as { [invId: string]: FullInventory });

        this.setState({
            response,
            selectedLibraryInventories,
            allSharedMaterialsTable: sharedMaterialsApiResult.data!,
            requestInventoryIdsToSelectedLibraryInventoryMap
        });
    }

    // ===============================================================================

    protected getLocalizedViewTitle(): string | null {
        return this.localizeString("Certification Consolidation Response");
    }

    // ===============================================================================

    private persistStateChange(mutation: () => Partial<LocalState>) {
        const newState = { ...this.state, ...mutation() };

        const response = { ...this.state.response! };

        for (let inventoryResponse of response.responseData.inventoryResponses) {
            inventoryResponse.responderLibraryInventoryId = newState.requestInventoryIdsToSelectedLibraryInventoryMap[inventoryResponse.requestorLibraryInventoryId]?.inventoryId ?? null;
        }

        this.setState({
            response
        });

        this._saveDataManager.save(response);
    }

    // ===============================================================================

    private getCertificationsToBeCreatedForPassport(passportInventory: FullInventory) {
        const map = this.state.requestInventoryIdsToSelectedLibraryInventoryMap;

        const inheritedNewCerts = this.state.response!.responseData.inventoryResponses
            .filter(inventoryResponse => map[inventoryResponse.requestorLibraryInventoryId]?.inventoryId === passportInventory.inventoryId)
            .selectMany(inventoryResponse => inventoryResponse.requestorCertificationsToConsolidate);

        return inheritedNewCerts;
    }

    // ===============================================================================

    private validateResponse() {
        return this.state.response?.responseData.inventoryResponses.every(resp => resp.responderLibraryInventoryId !== null) ?? false;
    }

    // ===============================================================================

    private renderBaseEditView() {
        const canSubmitResponse = this.validateResponse();

        const addNewPassportButton = (className: string) => <button type="button" className={`btn ${className}`} onClick={e => this.setState({ uiState: UiState.SelectLibraryInventory })} ><span className="glyphicon glyphicon-plus"></span> {this.localizeString("Add Passport")}</button>;

        return (
            <div id="edit-certifications-consolidation-response-root">
                <div className="tn-section-row">
                    <div className="tn-section-col-6">
                        <p>
                            <button type="button" className={`btn ${(canSubmitResponse ? "btn-toxnot-primary" : "btn-default")}`} disabled={!canSubmitResponse} onClick={async e => await this.submitResponseButton_click() }>{this.localizeString("Publish Passports & Submit Response")}</button>
                        </p>
                    </div>
                    <div className="tn-section-col-6">
                        {this.state.selectedLibraryInventories.length > 0 &&
                            <p>
                                {addNewPassportButton("btn-toxnot-primary")}
                            </p>}
                    </div>
                </div>
                <div className="tn-section-row">
                    <div className="tn-section-col-6">
                        <Heading>{this.localizeString("Consolidate these Products & Certifications into Passports")}</Heading>
                        <ContentContainer>
                            {this.localizeString("Your requestor is asking you to organize these certifications so they know which certifications apply to which products.")}
                        </ContentContainer>
                        <ContentContainer>
                            <div className="grouped-data">
                                {this.state.response!.responseData.inventoryResponses.map((responseInventory, index) => (
                                    <div key={responseInventory.requestorLibraryInventoryId} className="group">
                                        <div className="title">{responseInventory.requestorProductName}</div>
                                        <div className="content">
                                            <div className="labeled-container">
                                                <div>{this.localizeString("Certifications:")}</div>
                                                <div>
                                                    {responseInventory.requestorCertificationsToConsolidate.map((cert, index2) => (
                                                        <p key={index2}>{CertificationRendererManager.getFriendlyNameForCertificationType(cert.type, this.props.appServices)} - {cert.certificationExternalId}</p>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="labeled-container">
                                                <div>{this.localizeString("Add to Passport:")}</div>
                                                {!this.state.requestInventoryIdsToSelectedLibraryInventoryMap[responseInventory.requestorLibraryInventoryId]
                                                    ? <div className="subdued">{this.localizeString("Add a Passport to get started.")}</div>
                                                    : <div className="subdued"><DropdownMenu options={this.state.selectedLibraryInventories} getFriendlyNameForOptions={o => o.name} getUniqueValueForOption={o => encodeURIComponent(o.inventoryId)} onOptionSelected={o => this.libraryInventorySelector_optionSelected(responseInventory.requestorLibraryInventoryId, o)} /></div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </ContentContainer>
                    </div>
                    <div className="tn-section-col-6">
                        <Heading>{this.localizeString("Passports")}</Heading>
                        <ContentContainer>
                            {this.localizeString("Organize your certifications under Passports so your customers will know which certifications apply to which products.")}
                        </ContentContainer>
                        {this.state.selectedLibraryInventories.length === 0 &&
                            <ContentContainer>
                                <div className="alert alert-info">
                                    <p>
                                        {addNewPassportButton("btn-default")}
                                    </p>
                                    <p>
                                        {this.localizeString("You can easily share your data with customers by publishing Passports on the")} <a href="https://content.toxnot.com/toxnot-exchange" target="_blank">Passport Library</a>.
                                    </p>
                                    <p>
                                        {this.localizeString("A digital Product Passport lets you streamline & manage access to data about your products. You control who has access to Passports.")}
                                    </p>
                                    <p>
                                        {this.localizeString("You can organize your certifications under a Passport, which will let your customers understand which certifications apply to the SKU's they are buying.")}
                                    </p>
                                    <p>
                                        {this.localizeString("You can also use the Passport Library to share your Passports with buyer catalogs like")} mindfulMATERIALS.
                                    </p>
                                    <p>
                                        <a href="https://content.toxnot.com/toxnot-exchange" target="_blank">{this.localizeString("Learn more about Product & Material Passports on the")} Passport Library</a>
                                    </p>
                                </div>
                            </ContentContainer>
                        }
                        {this.state.selectedLibraryInventories.length > 0 &&
                            <ContentContainer>
                                <div className="grouped-data">
                                    {this.state.selectedLibraryInventories.map((selectedLibraryInventory, index) => (
                                        <div key={selectedLibraryInventory.inventoryId} className="group">
                                            <div className="title action-container"><div className="text">{selectedLibraryInventory.name}</div><div className="action"><button type="button" className="btn btn-toxnot-danger btn-sm" onClick={e => this.removeSelectedLibraryInventoryButton_click(selectedLibraryInventory.inventoryId)}><span className="glyphicon glyphicon-remove"></span></button></div></div>
                                            <div className="content">
                                                {(this.getCertificationsToBeCreatedForPassport(selectedLibraryInventory).length > 0) &&
                                                    <div className="labeled-container">
                                                        <div>{this.localizeString("Certifications that will be consolidated & added:")}</div>
                                                        <div>
                                                            {this.getCertificationsToBeCreatedForPassport(selectedLibraryInventory).map((cert, index2) => (
                                                                <p key={index2}>
                                                                    {CertificationRendererManager.getFriendlyNameForCertificationType(cert.type, this.props.appServices) + " - " + cert.certificationExternalId}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    </div>
                                                }
                                                {(selectedLibraryInventory.certifications.length > 0) &&
                                                    <div className="labeled-container">
                                                        <div>{this.localizeString("Existing Certifications:")}</div>
                                                        <div>
                                                            {selectedLibraryInventory.certifications.map((cert, index2) => (
                                                                <p key={index2}>
                                                                    {CertificationRendererManager.getFriendlyNameForCertificationType(cert.type, this.props.appServices) + " - " + cert.certificationExternalId}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    </div>
                                                }
                                                {(selectedLibraryInventory.soldItems.length > 0) &&
                                                    <div className="labeled-container">
                                                        <div>{this.localizeString("Sold Items:")}</div>
                                                        <div>
                                                            {selectedLibraryInventory.soldItems.map((soldItem, index3) => (
                                                                <>
                                                                    <p key={index3}>{soldItem.data.sku}</p>
                                                                    {soldItem.certifications.map((soldItemCert, index4) => (
                                                                        <p key={index4}>{CertificationRendererManager.getFriendlyNameForCertificationType(soldItemCert.type, this.props.appServices)} - {soldItemCert.certificationExternalId}</p>
                                                                    ))}
                                                                </>
                                                            ))}
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    ))}
                                    </div>
                            </ContentContainer>
                        }
                    </div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderSelectLibraryInventoryView = () => (
        <div className="tn-section-col-12">
            <div>
                <button type="button" className="btn btn-default" onClick={e => this.setState({ uiState: UiState.Editing })}>{this.localizeString("Cancel")}</button>
                {" "}
                <button type="button" className="btn btn-default" onClick={e => this.createNewlibraryProductButton_click() }>{this.localizeString("Create a new Product")}</button>
            </div>
            <SelectLibraryInventory onInventorySelected={inventory => this.selectLibraryInventory_inventorySelected(inventory)} />
        </div>
    );

    // ===============================================================================

    private renderCreateLibraryInventoryView = () => (
        <div className="tn-section-col-6">
            <div className="alert alert-info">{this.localizeString("You can create a new product in your library, to publish as a Passport, that specifies the certifications that go together.")}</div>
            <div className="form-group">
                <button type="button" className="btn btn-toxnot-primary" onClick={async e => await this.createNewlibraryProductSubmitButton_click()}>{this.localizeString("Create Product")}</button>
                {" "}
                <button type="button" className="btn btn-default" onClick={e => this.setState({ uiState: UiState.SelectLibraryInventory })}>{this.localizeString("Cancel")}</button>
            </div>
            <div className="form-group">
                <label>{this.localizeString("New Product Name")}</label>
                {this.state.uiState === UiState.NewProductNameBlank && <div className="alert alert-danger">{this.localizeString("You must provide a product name.")}</div>}
                <input type="text" className={this.getClassNameForProductName()} placeholder="" value={this.state.newProduct.name} onChange={e => this.newProductField_onChange({ name: e.target.value })} />
            </div>
            <div className="form-group">
                <label>{this.localizeString("Description (optional)")}</label>
                <input type="text" className="form-control" placeholder="" value={this.state.newProduct.description} onChange={e => this.newProductField_onChange({ description: e.target.value })} />
            </div>
            <div className="form-group">
                <label>{this.localizeString("Plant Name (optional)")}</label>
                <input type="text" className="form-control" placeholder="" value={this.state.newProduct.plant} onChange={e => this.newProductField_onChange({ plant: e.target.value })} />
            </div>
            <div className="form-group">
                <label>{this.localizeString("Supplier Name (optional)")}</label>
                <input type="text" className="form-control" placeholder="" value={this.state.newProduct.supplier} onChange={e => this.newProductField_onChange({ supplier: e.target.value })} />
            </div>
        </div>
    );

    // ===============================================================================

    private getClassNameForProductName() {
        return "form-control" + ((this.state.uiState === UiState.NewProductNameBlank && (this.state.newProduct.name ?? "") === "") ? " input-blank-warning" : "");
    }

    // ===============================================================================

    private renderRow = (element: JSX.Element) => <div className="tn-section-row">{element}</div>;

    // ===============================================================================

    private renderEditView() {
        switch (this.state.uiState) {
            case UiState.Editing: return this.renderBaseEditView();
            case UiState.SelectLibraryInventory: return this.renderRow(this.renderSelectLibraryInventoryView());
            case UiState.CreateLibraryInventory: return this.renderRow(this.renderCreateLibraryInventoryView());
            case UiState.NewProductNameBlank: return this.renderRow(this.renderCreateLibraryInventoryView());
            default: throw new Error(`Unknown ui state=${this.state.uiState}`);
        }
    }

    // ===============================================================================

    protected renderViewContent() {
        let content: JSX.Element | null = null;

        if (this.state.uiState === UiState.InitiatingSubmission) {
            content = this.renderRow(
                <div className="tn-section-col-12">
                    <p>
                        {this.localizeString("Submitting your response...")}
                    </p>
                    <div>
                        <LoadingSpinner size={LoadingSpinnerSize.Large} />
                    </div>
                </div>
            );
        }
        else if (this.state.uiState === UiState.SubmissionInProgress) {
            const notCompleteContent = (
                <>
                    <p>{this.localizeString("3E Exchange is publishing your Passports. This could take a bit.")}</p>
                    <p>{this.localizeString("We'll email you when it's done.")}</p>
                    <p>{this.localizeString("You can safely leave this page.")}</p>
                </>
            );

            const uniqueSharedMaterialIds = this.state.response!.responseData.inventoryResponses
                .filter(inv => inv.responderSharedMaterialId !== null)
                .map(inv => inv.responderSharedMaterialId!)
                .distinct();

            const completeContent = (
                <>
                    <p>
                        {this.localizeString("Your response has been submitted!")}
                    </p>
                    <p>
                        {this.localizeString("Your Passports:")}
                    </p>
                    {uniqueSharedMaterialIds.length === 0 && <LoadingSpinner />}
                    {uniqueSharedMaterialIds.map(sharedMaterialId => (
                        <p>
                            <Link key={sharedMaterialId} to={`/Exchange/Database/Detail/${encodeURIComponent(sharedMaterialId)}`}>{this.state.allSharedMaterialsTable[sharedMaterialId]?.materialName ?? this.localizeString("Unknown Passport")}</Link>
                        </p>
                    ))}
                </>
            );

            content = this.renderRow(
                <div className="tn-section-col-12">
                    <OfflineProcessProgress appServices={this.props.appServices} offlineProcessId={this.state.submissionOfflineProcessId!} notCompleteContent={notCompleteContent} completeContent={completeContent} onComplete={this.submissionProcess_complete} />
                    <p>
                        <button type="button" className="btn btn-toxnot-primary" onClick={e => window.location.href = "/Start"}>{this.localizeString("Done")}</button>
                    </p>
                </div>
            );
        }
        else {
            // editing
            content = this.renderEditView();
        }

        return content;
    }

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    private selectLibraryInventory_inventorySelected = async (selectedInventory: LibraryInventoriesGetInventory) => {
        this.setState({
            uiState: UiState.Editing
        });

        if (this.state.selectedLibraryInventories.some(i => i.inventoryId === selectedInventory.inventoryId)) {
            return;
        }

        this.setState({
            uiState: UiState.Loading
        });

        const apiResult = await this._serviceClient.getInventory(selectedInventory.inventoryId);

        if (!apiResult.success || apiResult.data === null) {
            this.enterFailedState();
            return;
        }

        const inventory = apiResult.data;

        const newSelectInventories = this.state.selectedLibraryInventories.concat([inventory]);

        const newRequestInventoryIdsToSelectedLibraryInventoryMap = { ...this.state.requestInventoryIdsToSelectedLibraryInventoryMap };

        // This is our first selected library
        if (newSelectInventories.length === 1) {
            for (let r of this.state.response!.responseData.inventoryResponses) {
                newRequestInventoryIdsToSelectedLibraryInventoryMap[r.requestorLibraryInventoryId] = newSelectInventories[0];
            }
        }

        this.persistStateChange(() => {
            const stateUpdate = {
                uiState: UiState.Editing,
                selectedLibraryInventories: newSelectInventories,
                requestInventoryIdsToSelectedLibraryInventoryMap: newRequestInventoryIdsToSelectedLibraryInventoryMap
            };
            this.setState(stateUpdate);
            return stateUpdate;
        });
    }

    // ===============================================================================

    private createNewlibraryProductButton_click = () => {
        this.setState({ uiState: UiState.CreateLibraryInventory });
    }

    // ===============================================================================

    private createNewlibraryProductSubmitButton_click = async () => {
        if (!this.state.newProduct.name || (this.state.newProduct.name).trim() === "") {
            this.setState({ uiState: UiState.NewProductNameBlank });
            return;
        }

        this.setState({ uiState: UiState.Loading });

        const apiResult = await this._serviceClient.createLibraryInventory(
            this.state.newProduct.name,
            this.state.newProduct.description,
            this.state.newProduct.supplier,
            this.state.newProduct.plant,
            false
        );

        if (!apiResult.success || apiResult.data === null) {
            this.enterFailedState();
            return;
        }

        const inventory = apiResult.data;

        this.persistStateChange(() => {
            const stateUpdate = {
                uiState: UiState.Editing,
                selectedLibraryInventories: this.state.selectedLibraryInventories.concat([inventory])
            }

            this.setState(stateUpdate);

            return stateUpdate;
        });
    }

    // ===============================================================================

    protected async saveDataToService(response: CertificationConsolidationResponse) {
        const apiResult = await this._serviceClient.updateCertificationConsolidationResponse(response);

        return apiResult.success;
    }

    // ===============================================================================

    private libraryInventorySelector_optionSelected = (responseInventoryId: string, selectedLibraryInventory: FullInventory) => {
        const newRequestInventoryIdsToSelectedLibraryInventoryMap = { ...this.state.requestInventoryIdsToSelectedLibraryInventoryMap };
        newRequestInventoryIdsToSelectedLibraryInventoryMap[responseInventoryId] = selectedLibraryInventory;

        this.persistStateChange(() => {
            const stateUpdate = {
                requestInventoryIdsToSelectedLibraryInventoryMap: newRequestInventoryIdsToSelectedLibraryInventoryMap
            };

            this.setState(stateUpdate);

            return stateUpdate;
        });
    }

    // ===============================================================================

    private newProductField_onChange = (updatedInfo: Partial<NewProductInfo>) => {
        this.setState({
            newProduct: {
                ...this.state.newProduct,
                ...updatedInfo
            }
        });
    }

    // ===============================================================================

    private removeSelectedLibraryInventoryButton_click = (inventoryIdToRemove: string) => {
        const indexToRemove = this.state.selectedLibraryInventories.findIndex(inv => inv.inventoryId === inventoryIdToRemove);

        if (indexToRemove === -1) {
            return;
        }

        const updatedSelectedLibraryInventories = [...this.state.selectedLibraryInventories];

        updatedSelectedLibraryInventories.splice(indexToRemove, 1);

        const newRequestInventoryIdsToSelectedLibraryInventoryMap = { ...this.state.requestInventoryIdsToSelectedLibraryInventoryMap };

        let requestInventoryIdsToRemove: string[] = [];

        for (let i of Object.keys(this.state.requestInventoryIdsToSelectedLibraryInventoryMap)) {
            if (this.state.requestInventoryIdsToSelectedLibraryInventoryMap[i]?.inventoryId === inventoryIdToRemove) {
                requestInventoryIdsToRemove.push(i);
            }
        }

        for (let idToRemove of requestInventoryIdsToRemove) {
            delete newRequestInventoryIdsToSelectedLibraryInventoryMap[idToRemove];
        }

        this.persistStateChange(() => {
            const stateUpdate = {
                selectedLibraryInventories: updatedSelectedLibraryInventories,
                requestInventoryIdsToSelectedLibraryInventoryMap: newRequestInventoryIdsToSelectedLibraryInventoryMap
            };

            this.setState(stateUpdate);

            return stateUpdate;
        });
    }

    // ===============================================================================

    private submitResponseButton_click = async () => {
        if (!this.validateResponse()) {
            return;
        }

        if (this._saveDataManager.needsToSave()) {
            this._saveDataManager.alertDataNeedsToBeSaved();
            return;
        }

        this.setState({
            uiState: UiState.InitiatingSubmission
        });

        const updateResult = await this._serviceClient.updateCertificationConsolidationResponse(this.state.response!, true);

        if (!updateResult.success) {
            this.enterFailedState();
            return;
        }

        this.setState({
            uiState: UiState.SubmissionInProgress,
            submissionOfflineProcessId: updateResult.data!.offlineProcessId
        });
    }

    // ===============================================================================

    private submissionProcess_complete = async () => {
        await this.fetchViewDataAndRefreshState();
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices })
)(withRouter(CertificationConsolidationResponseEditor) as any);
